import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  FileText,
  Download,
  CheckCircle,
  BookOpen,
  Link as LinkIcon,
  Loader,
  ChevronDown
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { FadeIn, StaggerChildren, StaggerItem, ScaleIn } from '../components/motion';
import { useAuth } from '../context/AuthContext';
import { analysisAPI } from '../services/api';
import toast from 'react-hot-toast';
import { Document, Packer, Paragraph, TextRun, AlignmentType, HeadingLevel } from 'docx';
import SEO from '../components/SEO';

const OutlineViewPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();

  const { topic, selectedEntries } = location.state || {};
  const [outline, setOutline] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (idx) => {
    setExpandedSections(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  // Initialize all sections as expanded when outline loads
  useEffect(() => {
    if (outline?.sections) {
      const allExpanded = {};
      outline.sections.forEach((_, idx) => { allExpanded[idx] = true; });
      setExpandedSections(allExpanded);
    }
  }, [outline]);

  useEffect(() => {
    if (!topic || !selectedEntries) {
      toast.error('Missing topic or entries data');
      navigate('/analyze');
      return;
    }

    generateOutline();
  }, [topic, selectedEntries]);

  const generateOutline = async () => {
    if (!currentUser) {
      toast.error('Please sign in to generate outlines');
      return;
    }

    setIsGenerating(true);

    try {
      const entryIds = selectedEntries.map(e => e.id);

      const result = await analysisAPI.generateOutline(
        entryIds,
        currentUser.uid,
        topic.title,
        'detailed'
      );

      setOutline(result);
      toast.success('Outline generated successfully!');
    } catch (error) {
      console.error('Error generating outline:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate outline');
      navigate('/analyze');
    } finally {
      setIsGenerating(false);
    }
  };

  const exportToWord = async () => {
    if (!outline) return;

    setIsExporting(true);

    try {
      const doc = new Document({
        sections: [{
          properties: {},
          children: [
            // Title
            new Paragraph({
              children: [
                new TextRun({
                  text: outline.topic,
                  bold: true,
                  size: 32,
                })
              ],
              heading: HeadingLevel.TITLE,
              alignment: AlignmentType.CENTER,
              spacing: { after: 400 }
            }),

            // Introduction
            new Paragraph({
              children: [
                new TextRun({
                  text: "Introduction",
                  bold: true,
                  size: 28,
                })
              ],
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 300, after: 200 }
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: outline.introduction,
                  size: 24,
                })
              ],
              spacing: { after: 300 }
            }),

            // Sections
            ...outline.sections.flatMap((section, idx) => {
              const sectionParagraphs = [
                // Section heading
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${idx + 1}. ${section.heading}`,
                      bold: true,
                      size: 28,
                    })
                  ],
                  heading: HeadingLevel.HEADING_1,
                  spacing: { before: 300, after: 200 }
                }),

                // Section description
                new Paragraph({
                  children: [
                    new TextRun({
                      text: section.description,
                      size: 24,
                      italics: true,
                    })
                  ],
                  spacing: { after: 200 }
                }),

                // Key points
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Key Points:",
                      bold: true,
                      size: 24,
                    })
                  ],
                  spacing: { before: 100, after: 100 }
                }),
                ...section.key_points.map(point =>
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: `• ${point}`,
                        size: 24,
                      })
                    ],
                    spacing: { after: 100 },
                    indent: { left: 360 }
                  })
                ),

                // Supporting sources
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Supporting Sources:",
                      bold: true,
                      size: 24,
                    })
                  ],
                  spacing: { before: 200, after: 100 }
                }),
                ...section.supporting_sources.map(sourceId => {
                  const source = outline.sources_used.find(s => s.id === sourceId);
                  return new Paragraph({
                    children: [
                      new TextRun({
                        text: source ? `• ${source.citation}` : `• Source ${sourceId}`,
                        size: 22,
                      })
                    ],
                    spacing: { after: 100 },
                    indent: { left: 360 }
                  });
                }),

                // Spacing after section
                new Paragraph({
                  children: [new TextRun({ text: "" })],
                  spacing: { after: 300 }
                })
              ];

              return sectionParagraphs;
            }),

            // Conclusion
            new Paragraph({
              children: [
                new TextRun({
                  text: "Conclusion",
                  bold: true,
                  size: 28,
                })
              ],
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 300, after: 200 }
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: outline.conclusion,
                  size: 24,
                })
              ],
              spacing: { after: 400 }
            }),

            // References
            new Paragraph({
              children: [
                new TextRun({
                  text: "References",
                  bold: true,
                  size: 28,
                })
              ],
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 400, after: 200 }
            }),
            ...outline.sources_used.map(source =>
              new Paragraph({
                children: [
                  new TextRun({
                    text: source.citation,
                    size: 24,
                  })
                ],
                spacing: { after: 200 }
              })
            )
          ]
        }]
      });

      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `outline-${outline.topic.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${Date.now()}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Outline exported successfully!');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export outline');
    } finally {
      setIsExporting(false);
    }
  };

  if (isGenerating) {
    return (
      <div className="min-h-screen bg-mesh flex items-center justify-center py-8">
        <SEO title="Outline" noIndex={true} />
        <FadeIn>
          <div className="text-center space-y-6">
            <ScaleIn>
              <div className="w-16 h-16 bg-gradient-to-br from-accent to-charcoal rounded-full flex items-center justify-center mx-auto">
                <Loader className="w-8 h-8 text-white animate-spin" />
              </div>
            </ScaleIn>
            <div>
              <h3 className="text-2xl font-bold text-secondary-900 mb-2">
                Generating Detailed Outline
              </h3>
              <p className="text-secondary-700">
                Mapping evidence to outline sections...
              </p>
            </div>
          </div>
        </FadeIn>
      </div>
    );
  }

  if (!outline) {
    return null;
  }

  return (
    <div className="min-h-screen bg-secondary-50/40 py-8">
      <SEO title="Outline" noIndex={true} />
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <button
              onClick={() => navigate('/analyze', { state: { selectedEntries } })}
              className="mt-1 p-1.5 text-secondary-500 hover:text-secondary-900 hover:bg-secondary-100 rounded-md transition-colors"
              aria-label="Back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl lg:text-3xl font-semibold text-secondary-900 tracking-tight">
                Detailed outline
              </h1>
              <p className="mt-1 text-sm text-secondary-500">
                Evidence-mapped structure with <span className="tabular-nums">{outline.sections.length}</span> sections
              </p>
            </div>
          </div>
            <motion.button
              onClick={exportToWord}
              disabled={isExporting}
              className="btn btn-primary"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isExporting ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Export to Word
                </>
              )}
            </motion.button>
        </div>

        {/* Success Message */}
        <FadeIn delay={0.1}>
          <div className="card bg-gradient-to-br from-green-50 to-khaki/10 mb-8">
            <div className="flex items-start space-x-4">
              <ScaleIn>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              </ScaleIn>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-secondary-900 mb-1">
                  Outline Generated Successfully!
                </h3>
                <p className="text-secondary-700 text-sm">
                  Synthesized from {outline.sources_used.length} sources with evidence mapping
                </p>
              </div>
            </div>
          </div>
        </FadeIn>

        {/* Outline Content */}
        <FadeIn delay={0.2}>
          <div className="glass-card">
            {/* Title */}
            <div className="mb-8 pb-6 border-b border-secondary-300/20">
              <h2 className="text-3xl font-bold text-secondary-900 text-center">
                {outline.topic}
              </h2>
            </div>

            {/* Introduction */}
            <FadeIn delay={0.3}>
              <div className="mb-8">
                <h3 className="text-xl font-bold text-secondary-900 mb-3 flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-primary" />
                  Introduction
                </h3>
                <p className="text-secondary-800 leading-relaxed">
                  {outline.introduction}
                </p>
              </div>
            </FadeIn>

            {/* Sections */}
            <StaggerChildren>
              <div className="space-y-6">
                {outline.sections.map((section, idx) => (
                  <StaggerItem key={idx}>
                    <div className="card-floating bg-pearl/20 rounded-lg overflow-hidden">
                      {/* Section header - clickable to toggle */}
                      <motion.button
                        onClick={() => toggleSection(idx)}
                        className="w-full p-6 pb-0 flex items-center justify-between text-left"
                        whileHover={{ backgroundColor: 'rgba(0,0,0,0.02)' }}
                      >
                        <h3 className="text-xl font-bold text-secondary-900">
                          {idx + 1}. {section.heading}
                        </h3>
                        <motion.div
                          animate={{ rotate: expandedSections[idx] ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <ChevronDown className="w-5 h-5 text-secondary-500" />
                        </motion.div>
                      </motion.button>

                      {/* Expandable content */}
                      <AnimatePresence initial={false}>
                        {expandedSections[idx] && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                            className="overflow-hidden"
                          >
                            <div className="p-6 pt-3">
                              <p className="text-secondary-700 italic mb-4">
                                {section.description}
                              </p>

                              <div className="mb-4">
                                <h4 className="text-sm font-semibold text-secondary-900 mb-2">Key Points:</h4>
                                <ul className="space-y-2">
                                  {section.key_points.map((point, pointIdx) => (
                                    <li key={pointIdx} className="flex items-start space-x-2">
                                      <span className="text-primary mt-1">•</span>
                                      <span className="text-secondary-800">{point}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>

                              <div className="bg-white rounded-lg p-4">
                                <h4 className="text-sm font-semibold text-secondary-900 mb-2 flex items-center">
                                  <LinkIcon className="w-4 h-4 mr-1 text-primary" />
                                  Supporting Sources:
                                </h4>
                                <div className="space-y-2">
                                  {section.supporting_sources.map((sourceId, srcIdx) => {
                                    const source = outline.sources_used.find(s => s.id === sourceId);
                                    return (
                                      <div key={srcIdx} className="text-sm text-secondary-700 pl-4 border-l-2 border-primary/30">
                                        {source ? source.citation : `Source ${sourceId}`}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </StaggerItem>
                ))}
              </div>
            </StaggerChildren>

            {/* Conclusion */}
            <FadeIn delay={0.3}>
              <div className="mt-8 pt-6 border-t border-secondary-300/20">
                <h3 className="text-xl font-bold text-secondary-900 mb-3 flex items-center">
                  <BookOpen className="w-5 h-5 mr-2 text-primary" />
                  Conclusion
                </h3>
                <p className="text-secondary-800 leading-relaxed">
                  {outline.conclusion}
                </p>
              </div>
            </FadeIn>
          </div>
        </FadeIn>

        {/* References */}
        <FadeIn delay={0.4}>
          <div className="glass-card mt-8">
            <h3 className="text-xl font-bold text-secondary-900 mb-4">
              References
            </h3>
            <StaggerChildren>
              <div className="space-y-3">
                {outline.sources_used.map((source, idx) => (
                  <StaggerItem key={idx}>
                    <div className="text-secondary-800 text-sm leading-relaxed">
                      {source.citation}
                    </div>
                  </StaggerItem>
                ))}
              </div>
            </StaggerChildren>
          </div>
        </FadeIn>

        {/* Action Buttons */}
        <FadeIn delay={0.5}>
          <div className="flex justify-center space-x-4 mt-8">
            <motion.button
              onClick={() => navigate('/analyze', { state: { selectedEntries } })}
              className="btn btn-outline"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Topics
            </motion.button>
            <motion.button
              onClick={() => navigate('/bibliography')}
              className="btn btn-outline"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Back to Sources
            </motion.button>
          </div>
        </FadeIn>
      </div>
    </div>
  );
};

export default OutlineViewPage;
